// What a screen hears from, and says to, the module's players (PlayableMedia): one event stream and one handle for
// every surface, so cast sync (hooks/useCastFollower) and the wall dock drive any kind the same way.
import type { CSSProperties, ReactNode, Ref } from "react";

export type MediaState = "playing" | "paused" | "buffering" | "ended";

export type MediaEvent =
  /** An embed mounted (before its first load): whether the provider's own controls are on. */
  | { type: "controls"; own: boolean }
  /** `key` is the media's URL: a report of an older media can be told apart. */
  | { type: "loaded"; key: string; durationMs?: number }
  | { type: "state"; key: string; state: MediaState; positionMs: number }
  /** `durationMs`: null = unknown or live; left out when the player does not say (embeds: see `loaded`). */
  | { type: "position"; key: string; positionMs: number; durationMs?: number | null }
  | { type: "ended"; key: string }
  /** The media cannot play (load or decode error, the provider refused it, the hosted player script is missing). */
  | { type: "error"; key: string };

export interface MediaHandle {
  play(): void;
  pause(): void;
  seek(ms: number): void;
  /** Elements only (drift nudging); a no-op for embeds and photos. */
  setRate(rate: number): void;
  /** Elements: drop the buffer and load again (a live stream rejoins its edge). */
  reload(): void;
  /** The element's reading now; null where the player reports by event only (embeds, photos). */
  sample(): { key: string; positionMs: number; state: MediaState; durationMs?: number } | null;
}

/** The screen's box around a surface with a picture (and around an audio face). */
export interface MediaFrame {
  ref?: Ref<HTMLDivElement>;
  testId?: string;
  className?: string;
  style?: CSSProperties;
  /** Wraps the media inside the frame (e.g. the shared full-screen controls). */
  wrap?(media: ReactNode): ReactNode;
  /** More of the screen's own inside the frame (a dialog, the full-screen button). */
  extra?: ReactNode;
}

/** The `<audio>` / `<video>` element's state in the follower's terms. */
export function videoState(el: HTMLMediaElement): MediaState {
  if (el.ended) return "ended";
  if (el.paused) return "paused";
  if (el.seeking || el.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) return "buffering";
  return "playing";
}

const noop = () => {};
/** A handle that does nothing (no player mounted yet, a photo). */
export const IDLE_HANDLE: MediaHandle = { play: noop, pause: noop, seek: noop, setRate: noop, reload: noop, sample: () => null };
